import type { Device, LinkNode } from 'data-layer';
import { LINK_TYPES } from 'data-layer/constants';
import {
  ButtonWithAppDeepLinking,
  ButtonWithDirectSoftwareDownload,
  ButtonWithLinkMasking,
  ButtonWithSoftwareDownload,
  ButtonWithOrderWaysDialog,
  ButtonWithPhotobookDownloadSoftware,
  ButtonWithAddToCart
} from './variants';
import type { ButtonCollectionProps, ButtonVariantProps } from './types';
import { ButtonSizes, ButtonVariants } from '@pixum/button';
import { CREATIVE_ID } from 'hooks';
import { getBaseUrl, isLinkOut, isLinkType, preserveUrlParams } from 'utils';

/**
 * Determines the button variant based on the index and creativeId.
 *
 * @param index - The index of the button in the collection.
 * @param creativeId - The creative ID to determine specific styling rules.
 * @returns The button variant to use.
 */
export function getButtonVariant(index: number, button: LinkNode, creativeId?: string): ButtonVariants {
  if (creativeId === CREATIVE_ID.MAGAZINE && isLinkType(button, LINK_TYPES.ADD_TO_CART)) {
    return ButtonVariants.TERTIARY;
  }

  if (creativeId === CREATIVE_ID.KEY_VISUAL) {
    return index === 0 ? ButtonVariants.STRONG : ButtonVariants.SECONDARY;
  }

  return index === 0 ? ButtonVariants.PRIMARY : ButtonVariants.SECONDARY;
}

/**
 * Determines the button size based on the button type and creativeId.
 *
 * @param button - The button data to check for specific types.
 * @param creativeId - The creative ID to determine specific sizing rules.
 * @returns The button size to use.
 */
export function getButtonSize(button: LinkNode, creativeId?: string) {
  // AddToCart buttons should always be MEDIUM regardless of creativeId
  if (isLinkType(button, LINK_TYPES.ADD_TO_CART)) {
    return ButtonSizes.MEDIUM;
  }

  if (creativeId === CREATIVE_ID.MAGAZINE) {
    return ButtonSizes.LARGE;
  }

  return ButtonSizes.MEDIUM;
}

/**
 * Transforms button properties for tracking and device context. This function can be customized or replaced via the
 * `buttonPropsTransformer` prop in `ButtonCollection`.
 *
 * @param button - The button data, including the URL and internal name.
 * @param device - The target device for the button ('mobile' or 'desktop').
 * @returns Partial button properties including id and device.
 */
function transformButtonVariantProps(
  button: LinkNode,
  device: Device,
  index: number,
  creativeId?: string
): ButtonVariantProps {
  return {
    id: button.internalName,
    device,
    link: button,
    variant: getButtonVariant(index, button, creativeId),
    size: getButtonSize(button, creativeId),
    creativeId
  };
}

/**
 * Renders a collection of buttons based on the provided props. It supports different button types like software
 * download links, app download deep links, and standard links with masking. The button variant is determined by the
 * index and creativeId. If you want to customize the button properties, you can provide your own
 * `buttonPropsTransformer` function.
 *
 * @param props - The props for the ButtonCollection component.
 * @returns A fragment containing the rendered buttons.
 */
export function ButtonCollection(props: ButtonCollectionProps) {
  const { buttons, device, constants, creativeId, buttonPropsTransformer } = props;
  const transform = buttonPropsTransformer ?? transformButtonVariantProps;

  return (
    <>
      {buttons
        .filter((button): button is LinkNode => button !== undefined && button !== null)
        .map((button: LinkNode, index: number) => {
          const { internalName } = button;

          // Direct download and add-to-cart buttons don't require URL
          if (!button?.url && !isLinkType(button, LINK_TYPES.DIRECT_SOFTWARE_DOWNLOAD, LINK_TYPES.ADD_TO_CART)) {
            return null;
          }

          const isExternal = isLinkOut(button.url, getBaseUrl());
          const enhancedButton = button.url && !isExternal ? { ...button, url: preserveUrlParams(button.url) } : button;

          if (isLinkType(button, LINK_TYPES.ORDER_WAYS_PRODUCT)) {
            // Special handling for order-ways dialog links
            return (
              <ButtonWithOrderWaysDialog
                key={`${internalName}-${index}`}
                {...transform(enhancedButton, device, index, creativeId)}
              />
            );
          } else if (isLinkType(button, LINK_TYPES.DOWNLOAD_SOFTWARE)) {
            // Special handling for software download links, which usually link to fotobuch/danke
            return (
              <ButtonWithSoftwareDownload
                key={`${internalName}-${index}`}
                device={device}
                link={enhancedButton}
                constants={constants}
              />
            );
          } else if (isLinkType(button, LINK_TYPES.DIRECT_SOFTWARE_DOWNLOAD)) {
            // Special handling for direct software download links (via PSI service)
            return (
              <ButtonWithDirectSoftwareDownload
                key={`${internalName}-${index}`}
                device={device}
                link={enhancedButton}
                constants={constants}
              />
            );
          } else if (isLinkType(button, LINK_TYPES.DOWNLOAD_APP_DEEPLINK)) {
            // Special handling for app download deep links
            return (
              <ButtonWithAppDeepLinking
                key={`${internalName}-${index}`}
                {...transform(enhancedButton, device, index, creativeId)}
              />
            );
          } else if (isLinkType(button, LINK_TYPES.PHOTOBOOK_SOFTWARE)) {
            return (
              <ButtonWithPhotobookDownloadSoftware
                key={`${internalName}-${index}`}
                {...transform(enhancedButton, device, index, creativeId)}
                device={device}
                link={enhancedButton}
                constants={constants}
              />
            );
          } else if (isLinkType(button, LINK_TYPES.ADD_TO_CART)) {
            return (
              <ButtonWithAddToCart
                key={`${internalName}-${index}`}
                {...transform(enhancedButton, device, index, creativeId)}
                device={device}
                link={enhancedButton}
                constants={constants}
              ></ButtonWithAddToCart>
            );
          }

          // Default case: standard button with link masking
          return (
            <ButtonWithLinkMasking
              key={`${enhancedButton.internalName}-${index}`}
              {...transform(enhancedButton, device, index, creativeId)}
            />
          );
        })}
    </>
  );
}
